import {
  type PanelDraftStageView,
  type PanelDraftView,
} from "@/shared/domain/boards/boardAuthoringService";

import {
  toolError,
  ToolErrorKind,
  type ClientToolExecutionResult,
} from "./ClientTools";

/** What a stage's `changeability` says the chat may do with it, sent with every draft
 *  the tools return. */
export const STAGE_CHANGEABILITY_NOTE =
  "A stage's changeability says whether the chat may change it: " +
  "'changeable' may be changed; 'read-outside-draft' is one of the draft's " +
  "stages and is also read by what read_by lists, so add a stage for the " +
  "draft instead; 'read-by-draft' is not one of the draft's stages and is " +
  "listed because a draft stage reads it.";

/** The draft as both draft tools return it: the authoring service's members
 *  in the snake_case the tools' inputs use. The panel and each stage's
 *  declaration keep the definition's spelling, because the model sends them
 *  back unchanged in operations. */
export function draftResult(draft: PanelDraftView): Record<string, unknown> {
  return {
    board_id: draft.boardId,
    editor_opening_id: draft.editorOpeningId,
    draft_id: draft.draftId,
    has_panel_kind: draft.hasPanelKind,
    opened_on_saved_panel: draft.openedOnSavedPanel,
    version: draft.version,
    panel: draft.panel,
    issues: draft.issues,
    stages: draft.stages.map(stageResult),
  };
}

function stageResult(stage: PanelDraftStageView): Record<string, unknown> {
  return {
    id: stage.id,
    name: stage.name,
    kind: stage.kind,
    changeability: stage.changeability,
    ...(stage.readBy === undefined ? {} : { read_by: stage.readBy }),
    ...(stage.runStatus === undefined ? {} : { run_status: stage.runStatus }),
    ...(stage.columns === undefined ? {} : { columns: stage.columns }),
    ...(stage.columnsOmitted === undefined
      ? {}
      : { columns_omitted: stage.columnsOmitted }),
    ...(stage.issues === undefined ? {} : { issues: stage.issues }),
    declaration: stage.declaration,
  };
}

/** No panel editor is open in this tab; `verb` is what the call meant to do
 *  with the draft. */
export function noEditorError(
  editorOpeningId: string,
  verb: "read" | "change",
): ClientToolExecutionResult {
  return toolError(
    `No panel editor is open in this tab, so there is no draft to ${verb} and nothing was changed. The editor closes when the user saves the draft, cancels it, or leaves the board. A saved draft is a panel on the board, which client_read_board reads.`,
    { editor_opening_id: editorOpeningId },
    ToolErrorKind.NOT_FOUND,
  );
}

/** The editor open in this tab is not the opening `editor_opening_id` names. */
export function otherEditorError(
  editorOpeningId: string,
  boardId: string,
): ClientToolExecutionResult {
  return toolError(
    `The panel editor open in this tab, on board "${boardId}", is not the opening editor_opening_id "${editorOpeningId}" names, as happens when the user closes an editor and opens another. Nothing was changed. Take editor_opening_id from misc_context.board.editing of the latest message and read the draft again.`,
    { editor_opening_id: editorOpeningId, board_id: boardId },
    ToolErrorKind.NOT_FOUND,
  );
}

/** The `editor_opening_id` property of both draft tools' input schemas. */
export const EDITOR_OPENING_ID_PROPERTY = {
  type: "string",
  description: "misc_context.board.editing.editor_opening_id.",
};

export function isEditorOpeningId(value: unknown): value is string {
  return typeof value === "string" && value !== "";
}

/** The refusal for an editor_opening_id input that {@link isEditorOpeningId}
 *  rejects. */
export function invalidEditorOpeningIdError(): ClientToolExecutionResult {
  return toolError(
    "editor_opening_id must be the editor_opening_id from misc_context.board.editing.",
    {},
    ToolErrorKind.VALIDATION,
  );
}
